import React, {
    Fragment,
    useState,
    useEffect,
    useCallback,
    useContext,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import { apiGet } from "helpers/Request";
import { useAuth } from "hooks/Auth";
import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import { transform as contentTransformer } from "schemas/Content";
import { getPaginationMeta } from "schemas/Util";
import ContentTable from "components/DesignSystem/ContentTable";
import ContentGrid from "components/DesignSystem/ContentGrid";
import Toolbar from "./Toolbar";
import Pagination from "components/General/Pagination";
import { AppContext } from "context/App";
import { updateParams as updateParamsUtil } from "Utils";
import KBarActions from "./KBarActions";
import { DashboardContext } from "context/Dashboard";
import Filterbar from "./Filterbar";
import ErrorCard from "Cards/ErrorCard";
import { useSelector } from "react-redux";

export default function DashboardScreen() {
    // load hooks
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    const { setHomeNav, setCurrentNav } = useContext(AppContext);
    const settings = useSelector((state) => state.contentFlow?.settings);

    // set state
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [contentItems, setContentItems] = useState([]);
    const [paginationMeta, setPaginationMeta] = useState({});
    const [selectedItems, setSelectedItems] = useState([]);
    const [queryParams, setQueryParams] = useState(
        new URLSearchParams(location.search)
    );

    const loadContent = useCallback(() => {
        setLoading(true);
        setHasError(false);
        setSelectedItems([]);
        const defaultParams = {
            per_page: 20,
            order: "modified_at",
            dir: "desc",
            mode: "default",
        };
        const definedParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );

        apiGet(
            "/v4/content",
            auth.publication,
            Object.assign({}, defaultParams, definedParams)
        )
            .then((res) => {
                setContentItems(contentTransformer(res));
                setPaginationMeta(getPaginationMeta(res));
                setLoading(false);
            })
            .catch((err) => {
                setHasError(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, auth.publication]);

    const updateParams = (key, val = null) => {
        history.replace(updateParamsUtil(location, key, val));
    };

    const onSortClick = (sortKey) => {
        let sortDir = "desc";
        if (queryParams.has("order") && queryParams.get("order") === sortKey) {
            if (queryParams.has("dir") && queryParams.get("dir") === "asc") {
                sortDir = "desc";
            } else {
                sortDir = "asc";
            }
        }

        updateParams({ order: sortKey, dir: sortDir });
    };

    const onSingleSelect = (item, checked) => {
        if (checked) {
            setSelectedItems([...selectedItems, item.uuid]);
        } else {
            setSelectedItems(selectedItems.filter((i) => i !== item.uuid));
        }
    };
    const onAllSelect = (items) => {
        setSelectedItems(items.map((item) => item.uuid));
    };

    useEffect(() => {
        setCurrentNav("home");
    }, [setCurrentNav]);

    useEffect(() => {
        loadContent();
        const params = new URLSearchParams(location.search);
        setQueryParams(params);
        setHomeNav(location.pathname + "?" + params.toString());
    }, [location, loadContent, setHomeNav]);

    return (
        <DashboardContext.Provider
            value={{
                loadContent,
                paginationMeta,
                selectedItems,
                queryParams,
            }}
        >
            <MainBlock>
                <KBarActions />
                <Toolbar />
                <div className="mb-4 items-center justify-center lg:flex lg:justify-between">
                    <Filterbar />
                    <div className="">
                        <Pagination
                            maxPages={2}
                            variant="xs"
                            basePath="dashboard"
                            {...paginationMeta}
                        />
                    </div>
                </div>
                <div>
                    {hasError ? (
                        <ErrorCard title="Unable to load content from CEO">
                            <p>
                                The CEO server returned an error, this usually
                                means a problem in the search server. You can{" "}
                                <button
                                    type="button"
                                    onClick={() => loadContent()}
                                    className="text-green-600 underline"
                                >
                                    try again
                                </button>
                                , or{" "}
                                <a
                                    href="https://snworks.zendesk.com/hc/en-us/requests/new"
                                    className="text-green-600 underline"
                                    rel="nofollow noopener noreferrer"
                                    target="_blank"
                                >
                                    contact support
                                </a>
                                .
                            </p>
                        </ErrorCard>
                    ) : (
                        <Fragment>
                            {queryParams.get("view") === "grid" ? (
                                <div className="rounded bg-white p-2">
                                    <ContentGrid
                                        loading={loading}
                                        items={contentItems}
                                        onSelect={onSingleSelect}
                                        onSelectAll={onAllSelect}
                                        onSortClick={onSortClick}
                                        selectedItems={selectedItems}
                                        sortedColumn={
                                            queryParams.has("order")
                                                ? queryParams.get("order")
                                                : "created_at"
                                        }
                                    />
                                </div>
                            ) : (
                                <ContentTable
                                    loading={loading}
                                    items={contentItems}
                                    onSelect={onSingleSelect}
                                    onSelectAll={onAllSelect}
                                    onSortClick={onSortClick}
                                    selectedItems={selectedItems}
                                    sortedColumn={
                                        queryParams.has("order")
                                            ? queryParams.get("order")
                                            : "created_at"
                                    }
                                />
                            )}
                        </Fragment>
                    )}
                    <div className="my-4">
                        <Pagination
                            basePath="dashboard"
                            {...paginationMeta}
                            onPageChange={() => {
                                window.scrollTo(0, 0);
                            }}
                        />
                    </div>
                </div>
            </MainBlock>
        </DashboardContext.Provider>
    );
}
